/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { ColumnInstance } from 'react-table';
import { Text, Tag, BrandColors } from '@blend/enterprise-components';
import { formatIsoDate, titleCase } from '@utils/formatting';
import { toUpper } from '@utils/lodash';
const styles = StyleSheet.create({
  tableHeaderContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  tag: { margin: 0 },
});

// TODO: Update to real entry type and move to a global place
export interface JournalEntry {
  id: string;
  date: string;
  signers: string;
  location: string;
  state: string;
  status: string;
}

const TableHeader: React.SFC<{ column: ColumnInstance<JournalEntry> }> = ({ column }) => {
  return (
    <div className={css(styles.tableHeaderContainer)}>
      <Text color={BrandColors.NIGHT} fontSize="sm" weight="semibold">
        {toUpper(column.id)}
      </Text>
    </div>
  );
};

const TableCell: React.FC<{ value: string }> = ({ value }) => {
  return <Text>{value}</Text>;
};

const StatusCell: React.FC<{ value: string }> = ({ value }) => {
  const tagTheme = value === 'Pending' ? 'warning' : 'default';
  return <Tag text={toUpper(value)} theme={tagTheme} />;
};

interface BaseColumnConfig {
  Cell: React.FC<{ value: string }>;
  Header: React.SFC<{ column: ColumnInstance<JournalEntry> }>;
}

const baseColumnConfig: BaseColumnConfig = {
  Cell: TableCell,
  Header: TableHeader,
};

export const columnConfigurations = [
  {
    ...baseColumnConfig,
    accessor: (entry: JournalEntry) => formatIsoDate(entry.date),
    id: 'Closing Date',
    width: '14%',
  },
  {
    ...baseColumnConfig,
    accessor: (entry: JournalEntry) => entry.signers,
    id: 'Signer Name',
    width: '30%',
  },
  {
    ...baseColumnConfig,
    accessor: () => titleCase('Purchase'),
    id: 'Signing',
    width: '12%',
  },
  {
    ...baseColumnConfig,
    accessor: (entry: JournalEntry) => titleCase(entry.location),
    id: 'Location',
    width: '18%',
  },
  {
    ...baseColumnConfig,
    accessor: (entry: JournalEntry) => entry.state,
    id: 'Property State',
    width: '10%',
  },
  {
    ...baseColumnConfig,
    accessor: (entry: JournalEntry) => entry.status,
    Cell: StatusCell,
    id: 'Status',
    width: '24%',
  },
];
